// src/components/Login/LoginPremium.jsx
// ✅ VERSÃO ULTRA PREMIUM — PropZyn by Ihea leads imoveis
// ✅ GOOGLE LOGIN FUNCIONAL - NIVEL AIRBNB/SPOTIFY
// 🔥 ENTERPRISE: Flujo corregido - Sin doble llamada a authService
// 🔥 V2: BLINDADO — useAuthBlindado con timeout, rate limit, retry, network check
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/useAuth';
import { useAuthBlindado } from '../../hooks/useAuthBlindado';
import {
  Building2,
  LogIn,
  UserPlus,
  Key,
  Mail,
  Lock,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Sparkles,
  Shield,
  Zap,
  Eye,
  EyeOff,
  Star,
  Rocket,
  User,
  TrendingUp,
  Globe,
  BarChart3,
  WifiOff
} from 'lucide-react';
import SignUp from './SignUp';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import VerifyEmail from './VerifyEmail';
import Onboarding from './Onboarding';
import SocialButtons from './SocialButtons';
import PasswordStrength from './PasswordStrength';
import TermsCheckbox from './TermsCheckbox';
import { useIsMobile } from '../../hooks/useMediaQuery';

// ============================================
// 🎨 CONSTANTES E CONFIGURAÇÕES
// ============================================
const MODES = {
  LOGIN: 'login',
  SIGNUP: 'signup',
  FORGOT_PASSWORD: 'forgot-password',
  RESET_PASSWORD: 'reset-password',
  VERIFY_EMAIL: 'verify-email',
  ONBOARDING: 'onboarding'
};

const DEMO_ACCOUNTS = [
  {
    id: 'admin',
    email: 'admin@imoveispro.com',
    password: '123456',
    role: 'Admin',
    label: 'Administrador',
    color: 'from-violet-500 to-purple-600',
    borderColor: 'hover:border-violet-400',
    icon: Shield,
    features: ['Acesso total', 'Configurações', 'Relatórios']
  },
  {
    id: 'corretor',
    email: 'corretor@imoveispro.com',
    password: '123456',
    role: 'Corretor',
    label: 'Corretor',
    color: 'from-sky-500 to-blue-600',
    borderColor: 'hover:border-sky-400',
    icon: TrendingUp,
    features: ['Gestão clientes', 'Imóveis', 'Tarefas']
  },
  {
    id: 'usuario',
    email: 'ana@imoveispro.com',
    password: '123456',
    role: 'Usuário',
    label: 'Usuário',
    color: 'from-emerald-500 to-teal-600',
    borderColor: 'hover:border-emerald-400',
    icon: User,
    features: ['Visualização', 'Contato', 'Favoritos']
  }
];

// Features para o painel esquerdo
const LEFT_FEATURES = [
  {
    icon: Globe,
    title: 'Multi-tenancy enterprise',
    desc: 'Isolamento total de dados por organização',
    gradient: 'from-blue-500 to-indigo-600'
  },
  {
    icon: Zap,
    title: 'Offline-first & real-time',
    desc: 'Sincronização instantânea em qualquer conexão',
    gradient: 'from-amber-500 to-orange-500'
  },
  {
    icon: Sparkles,
    title: 'White label completo',
    desc: 'Identidade visual 100% personalizável',
    gradient: 'from-pink-500 to-rose-500'
  },
  {
    icon: BarChart3,
    title: 'Analytics executivo',
    desc: 'Inteligência de negócio em tempo real',
    gradient: 'from-emerald-500 to-cyan-500'
  },
  {
    icon: Rocket,
    title: 'Automação por IA',
    desc: 'Captação, vendas e follow-up automatizados',
    gradient: 'from-violet-500 to-purple-600'
  }
];

// ============================================
// 🎨 ESTILOS CSS EMBUTIDOS (injeção no head)
// ============================================
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,300;1,9..40,400&family=Syne:wght@400;500;600;700;800&display=swap');

  .propzyn-root * { box-sizing: border-box; }

  .propzyn-root {
    font-family: 'DM Sans', sans-serif;
    --brand-blue: #2563eb;
    --brand-purple: #7c3aed;
    --brand-indigo: #4f46e5;
    --brand-gradient: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  }

  .font-syne { font-family: 'Syne', sans-serif; }

  /* Noise texture overlay */
  .noise-bg::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
    opacity: 0.4;
    mix-blend-mode: overlay;
  }

  /* Animated gradient orbs */
  @keyframes orb-float-1 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(30px, -20px) scale(1.05); }
    66% { transform: translate(-20px, 15px) scale(0.97); }
  }
  @keyframes orb-float-2 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(-25px, 20px) scale(0.95); }
    66% { transform: translate(15px, -25px) scale(1.08); }
  }
  @keyframes orb-float-3 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(20px, 30px) scale(1.03); }
  }

  .orb-1 { animation: orb-float-1 14s ease-in-out infinite; }
  .orb-2 { animation: orb-float-2 18s ease-in-out infinite; }
  .orb-3 { animation: orb-float-3 12s ease-in-out infinite; }

  /* Grid pattern */
  .grid-pattern {
    background-image:
      linear-gradient(rgba(37, 99, 235, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(37, 99, 235, 0.04) 1px, transparent 1px);
    background-size: 40px 40px;
  }

  /* Input focus glow */
  .input-premium:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12), 0 1px 2px rgba(0,0,0,0.05);
  }

  /* Scrollbar slim */
  .scrollbar-thin::-webkit-scrollbar { width: 4px; }
  .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
  .scrollbar-thin::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.4); border-radius: 2px; }

  /* Button shimmer */
  @keyframes shimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  .btn-shimmer {
    background: linear-gradient(
      90deg,
      #2563eb 0%,
      #4f46e5 30%,
      #7c3aed 50%,
      #4f46e5 70%,
      #2563eb 100%
    );
    background-size: 200% auto;
    animation: shimmer 4s linear infinite;
  }
  .btn-shimmer:hover {
    animation-duration: 2s;
  }

  /* Fade line */
  .fade-line {
    background: linear-gradient(90deg, transparent, rgba(148,163,184,0.3), transparent);
  }

  /* Stat counter card */
  .stat-card {
    background: rgba(255,255,255,0.06);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255,255,255,0.1);
  }

  /* Mobile native feel */
  @media (max-width: 1023px) {
    .propzyn-root {
      -webkit-tap-highlight-color: transparent;
    }
    .input-premium {
      font-size: 16px !important; /* Prevent iOS zoom */
    }
  }
`;

// ============================================
// 🧩 COMPONENTE: Logo PropZyn
// ============================================
const PropZynLogo = ({ size = 'md', dark = false }) => {
  const sizes = {
    sm: { icon: 'w-5 h-5', container: 'p-2.5', title: 'text-lg', sub: 'text-[9px]', gap: 'gap-2.5' },
    md: { icon: 'w-6 h-6', container: 'p-3', title: 'text-xl', sub: 'text-[10px]', gap: 'gap-3' },
    lg: { icon: 'w-8 h-8', container: 'p-3.5', title: 'text-3xl', sub: 'text-[10px]', gap: 'gap-4' }
  };
  const s = sizes[size];
  return (
    <div className={`flex items-center ${s.gap}`}>
      <div className="relative flex-shrink-0">
        <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-violet-600 rounded-xl opacity-25 blur-md" />
        <div className={`relative ${s.container} bg-gradient-to-br from-blue-600 to-violet-600 rounded-xl shadow-lg`}>
          <Building2 className={`${s.icon} text-white`} />
        </div>
      </div>
      <div>
        <div className={`font-syne font-bold ${s.title} leading-none ${dark ? 'text-slate-900' : 'text-white'}`}>
          <span className={dark ? 'text-slate-900' : 'text-white'}>Prop</span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400">Zyn</span>
        </div>
        <div className={`${s.sub} uppercase tracking-[0.25em] font-medium mt-0.5 ${dark ? 'text-slate-500' : 'text-white/50'}`}>
          by Ihea leads imóveis
        </div>
      </div>
    </div>
  );
};

// ============================================
// 🧩 COMPONENTE: Stats ticker (lado esquerdo)
// ============================================
const StatsBadges = () => {
  const stats = [
    { value: '12k+', label: 'Imóveis ativos' },
    { value: '98%', label: 'Uptime garantido' },
    { value: '340+', label: 'Imobiliárias' }
  ];
  return (
    <div className="flex gap-3">
      {stats.map((s, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 + i * 0.1 }}
          className="stat-card rounded-xl px-4 py-3 flex flex-col"
        >
          <span className="font-syne font-bold text-white text-lg leading-none">{s.value}</span>
          <span className="text-white/40 text-[10px] mt-0.5 tracking-wide">{s.label}</span>
        </motion.div>
      ))}
    </div>
  );
};

// ============================================
// 🎨 COMPONENTE PRINCIPAL
// ============================================
const LoginPremium = ({
  initialMode = MODES.LOGIN,
  redirectAfterLogin = '/',
  onModeChange
}) => {
  const navigate = useNavigate();
  const { login, signUp, loadingAuth, loginWithGoogle } = useAuth();
  const authBlindado = useAuthBlindado();
  const isMobile = useIsMobile();
  const styleRef = useRef(null);

  // Inject global styles once
  useEffect(() => {
    if (!document.getElementById('propzyn-styles')) {
      const el = document.createElement('style');
      el.id = 'propzyn-styles';
      el.textContent = GLOBAL_STYLES;
      document.head.appendChild(el);
    }
  }, []);

  // ============================================
  // 📊 ESTADOS
  // ============================================
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resetToken, setResetToken] = useState(null);
  const [verifyToken, setVerifyToken] = useState(null);
  const [userData, setUserData] = useState(null);

  // ============================================
  // 🔍 EFEITOS
  // ============================================
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const mode_param = params.get('mode');
    if (token) {
      if (mode_param === 'reset') {
        setResetToken(token);
        setMode(MODES.RESET_PASSWORD);
      } else if (mode_param === 'verify') {
        setVerifyToken(token);
        setMode(MODES.VERIFY_EMAIL);
      }
    }
  }, []);

  useEffect(() => {
    if (error || successMessage) {
      const timer = setTimeout(() => {
        setError('');
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, successMessage]);

  // ============================================
  // 🎯 HANDLERS DE AUTENTICAÇÃO (BLINDADOS V2)
  // ============================================
  
  // 🔥 V2: Login com Email/Senha — Blindado com timeout + rate limit + retry
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }
    
    // Verificar conexão antes de tentar
    if (!navigator.onLine) {
      setError('Sem conexão com a internet. Verifique sua rede.');
      return;
    }

    setLoading(true);
    setError('');

    // 🔥 V2: Usar o hook blindado (timeout 15s, rate limit, retry 3x)
    const result = await authBlindado.login(email, password);

    if (result.success) {
      setSuccessMessage('Login realizado com sucesso!');
      setTimeout(() => navigate(redirectAfterLogin), 800);
    } else {
      setError(result.error || 'Credenciais inválidas. Verifique e tente novamente.');
    }

    setLoading(false);
  };

  // 🔥 V2: Demo Login — Blindado com timeout + rate limit
  const handleDemoLogin = async (demoEmail, demoPassword) => {
    setEmail(demoEmail);
    setPassword(demoPassword);
    
    if (!navigator.onLine) {
      setError('Sem conexão com a internet. Verifique sua rede.');
      return;
    }

    setLoading(true);
    setError('');

    const result = await authBlindado.login(demoEmail, demoPassword);

    if (result.success) {
      setSuccessMessage('Bem-vindo ao ambiente de demonstração!');
      setTimeout(() => navigate(redirectAfterLogin), 800);
    } else {
      setError(result.error || 'Erro no login demo');
    }

    setLoading(false);
  };

  // 🔥 V2: Google Login — Blindado com timeout + rate limit + tratamento de erros
  const handleGoogleLoginSuccess = async (data) => {
    if (!data.success || !data.user) {
      setError('Login com Google falhou');
      return;
    }

    if (!navigator.onLine) {
      setError('Sem conexão com a internet. Verifique sua rede.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const success = await loginWithGoogle(data.user);
      if (success) {
        setSuccessMessage('Autenticado com Google!');
        setTimeout(() => navigate(redirectAfterLogin), 800);
      } else {
        setError('Não foi possível completar o login com Google');
      }
    } catch (err) {
      console.error('❌ Google login error:', err);
      if (err.code === 'auth/popup-closed-by-user') setError('Login cancelado pelo usuário');
      else if (err.code === 'auth/popup-blocked') setError('Popup bloqueado. Permita janelas emergentes.');
      else if (err.code === 'auth/account-exists-with-different-credential') setError('Email já registrado com outro método de login');
      else if (err.code === 'auth/network-request-failed') setError('Falha de conexão. Verifique sua internet.');
      else if (err.code === 'custom/timeout') setError('Tempo limite excedido. Tente novamente.');
      else if (err.code === 'custom/rate-limited') setError('Muitas tentativas. Aguarde 30 segundos.');
      else setError('Erro ao conectar com Google. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUpSuccess = (user) => {
    setUserData(user);
    setSuccessMessage('Conta criada! Verifique seu email para ativar.');
    if (user.emailVerified) {
      setTimeout(() => setMode(MODES.ONBOARDING), 2000);
    }
  };
  const handleForgotPasswordSuccess = (email, token) => {
    setSuccessMessage(`Instruções enviadas para ${email}`);
    if (process.env.NODE_ENV === 'development') console.log('🔑 Reset token:', token);
  };
  const handleResetPasswordSuccess = () => {
    setSuccessMessage('Senha redefinida com sucesso!');
    setTimeout(() => setMode(MODES.LOGIN), 2000);
  };
  const handleVerifyEmailSuccess = () => {
    setSuccessMessage('Email verificado com sucesso!');
    setTimeout(() => setMode(MODES.ONBOARDING), 2000);
  };
  const handleOnboardingComplete = () => {
    setSuccessMessage('Configuração concluída! Bem-vindo.');
    setTimeout(() => navigate(redirectAfterLogin), 1500);
  };

  // ============================================
  // 🎨 RENDER: FORMULÁRIO DE LOGIN
  // ============================================
  const renderLogin = () => (
    <motion.div
      key="login"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      className="space-y-5"
    >
      {/* Botão Google */}
      <SocialButtons
        onSuccess={handleGoogleLoginSuccess}
        disabled={loading}
        mode="auto"
      />

      {/* Divisor */}
      <div className="relative flex items-center gap-3">
        <div className="flex-1 fade-line h-px" />
        <span className="text-xs text-slate-400 font-medium whitespace-nowrap px-1">ou entre com email</span>
        <div className="flex-1 fade-line h-px" />
      </div>

      {/* 🔥 V2: Indicador offline */}
      {!navigator.onLine && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
          <WifiOff className="w-4 h-4 text-amber-600" />
          <span className="text-xs text-amber-700">Sem conexão com a internet</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleLogin} className="space-y-4">
        {/* Email */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            E-mail
          </label>
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-premium w-full h-11 pl-10 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 text-sm transition-all"
              placeholder="seu@email.com"
              disabled={loading}
              autoComplete="email"
            />
          </div>
        </div>

        {/* Senha */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            Senha
          </label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-premium w-full h-11 pl-10 pr-11 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 text-sm transition-all"
              placeholder="••••••••"
              disabled={loading}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Lembrar + Esqueceu */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div
              onClick={() => setRememberMe(!rememberMe)}
              className={`w-4 h-4 rounded flex items-center justify-center border transition-all cursor-pointer ${
                rememberMe
                  ? 'bg-blue-600 border-blue-600'
                  : 'bg-white border-slate-300'
              }`}
            >
              {rememberMe && (
                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12">
                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <span className="text-sm text-slate-600">Manter conectado</span>
          </label>
          <button
            type="button"
            onClick={() => setMode(MODES.FORGOT_PASSWORD)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
          >
            Esqueceu a senha?
          </button>
        </div>

        {/* Botão Entrar */}
        <motion.button
          type="submit"
          disabled={loading || !navigator.onLine}
          whileHover={!loading ? { scale: 1.01 } : {}}
          whileTap={!loading ? { scale: 0.99 } : {}}
          className={`
            w-full h-12 rounded-xl font-semibold text-sm
            flex items-center justify-center gap-2.5
            transition-all duration-200
            ${loading || !navigator.onLine
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
              : 'btn-shimmer text-white shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30'
            }
          `}
        >
          {loading ? (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}
                className="w-4 h-4 border-2 border-slate-300 border-t-blue-500 rounded-full"
              />
              Autenticando...
            </>
          ) : !navigator.onLine ? (
            <>
              <WifiOff className="w-4 h-4" />
              Sem conexão
            </>
          ) : (
            <>
              <LogIn className="w-4 h-4" />
              Acessar plataforma
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </motion.button>
      </form>

      {/* Contas demo */}
      <div className="pt-1">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2.5 text-center">
          Acesso de demonstração
        </p>
        <div className="grid grid-cols-3 gap-2">
          {DEMO_ACCOUNTS.map((account) => {
            const Icon = account.icon;
            return (
              <motion.button
                key={account.id}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleDemoLogin(account.email, account.password)}
                disabled={loading || !navigator.onLine}
                className={`
                  group relative p-3 rounded-xl border border-slate-200 ${account.borderColor}
                  bg-white transition-all duration-200
                  hover:shadow-sm disabled:opacity-50
                `}
              >
                <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${account.color} flex items-center justify-center mx-auto mb-1.5 shadow-sm`}>
                  <Icon className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="block text-[11px] font-semibold text-slate-700 leading-none">
                  {account.label}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Link cadastro */}
      <p className="text-center text-sm text-slate-500 pt-1">
        Novo por aqui?{' '}
        <button
          onClick={() => setMode(MODES.SIGNUP)}
          className="font-semibold text-blue-600 hover:text-blue-700 transition-colors"
        >
          Criar conta gratuita →
        </button>
      </p>
    </motion.div>
  );

  const renderSignUp = () => (
    <motion.div key="signup" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}>
      <SignUp onSuccess={handleSignUpSuccess} onLoginRedirect={() => setMode(MODES.LOGIN)} />
    </motion.div>
  );
  const renderForgotPassword = () => (
    <motion.div key="forgot" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}>
      <ForgotPassword onBackToLogin={() => setMode(MODES.LOGIN)} onEmailSent={handleForgotPasswordSuccess} />
    </motion.div>
  );
  const renderResetPassword = () => (
    <motion.div key="reset" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}>
      <ResetPassword token={resetToken} onSuccess={handleResetPasswordSuccess} onBackToLogin={() => setMode(MODES.LOGIN)} />
    </motion.div>
  );
  const renderVerifyEmail = () => (
    <motion.div key="verify" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}>
      <VerifyEmail token={verifyToken} onSuccess={handleVerifyEmailSuccess} onBackToLogin={() => setMode(MODES.LOGIN)} />
    </motion.div>
  );
  const renderOnboarding = () => (
    <motion.div key="onboarding" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
      <Onboarding user={userData} onComplete={handleOnboardingComplete} onSkip={() => navigate(redirectAfterLogin)} />
    </motion.div>
  );

  // ============================================
  // 🎨 RENDER PRINCIPAL
  // ============================================
  return (
    <div className="propzyn-root min-h-screen flex" style={{ background: '#f8fafc' }}>

      {/* ===== LADO ESQUERDO — BRANDING DARK ===== */}
      <div className="hidden lg:flex lg:w-[48%] xl:w-[52%] relative overflow-hidden flex-col">

        {/* Dark gradient background */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(145deg, #0f172a 0%, #1e1b4b 40%, #0f172a 100%)'
        }} />

        {/* Grid pattern */}
        <div className="absolute inset-0 grid-pattern opacity-60" />

        {/* Gradient orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="orb-1 absolute w-[500px] h-[500px] rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #3b82f6, transparent 70%)', top: '-100px', left: '-100px' }} />
          <div className="orb-2 absolute w-[600px] h-[600px] rounded-full opacity-15"
            style={{ background: 'radial-gradient(circle, #7c3aed, transparent 70%)', bottom: '-150px', right: '-100px' }} />
          <div className="orb-3 absolute w-[300px] h-[300px] rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #06b6d4, transparent 70%)', bottom: '30%', left: '30%' }} />
        </div>

        {/* Noise overlay */}
        <div className="noise-bg absolute inset-0 pointer-events-none" />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full p-12 xl:p-16">

          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <PropZynLogo size="lg" />
          </motion.div>

          {/* Hero text */}
          <motion.div
            className="mt-16 xl:mt-20 flex-1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <p className="text-[11px] uppercase tracking-[0.3em] text-blue-400/80 font-medium mb-5">
              · Plataforma imobiliária enterprise ·
            </p>
            <h2 className="font-syne text-5xl xl:text-6xl font-bold text-white leading-[1.05] mb-3">
              Gestão que
            </h2>
            <h2 className="font-syne text-5xl xl:text-6xl font-bold leading-[1.05]"
              style={{ WebkitTextStroke: '1.5px rgba(255,255,255,0.15)', color: 'transparent' }}>
              transforma
            </h2>
            <h2 className="font-syne text-5xl xl:text-6xl font-bold text-white leading-[1.05]">
              resultados.
            </h2>

            <p className="mt-6 text-slate-400 text-base font-light max-w-sm leading-relaxed">
              A plataforma mais completa do mercado imobiliário brasileiro. Do lead ao contrato, tudo integrado.
            </p>

            {/* Stats */}
            <div className="mt-10">
              <StatsBadges />
            </div>

            {/* Feature list */}
            <div className="mt-12 space-y-1">
              {LEFT_FEATURES.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + idx * 0.08 }}
                    className="group flex items-center gap-4 p-3 rounded-2xl hover:bg-white/5 transition-all duration-300 cursor-default"
                  >
                    <div className={`shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center shadow-lg`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white/90 leading-none">{feature.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{feature.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            className="mt-10 pt-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div className="flex items-center justify-between">
              <div className="text-[11px] text-slate-600 space-x-3">
                <span>© {new Date().getFullYear()} PropZyn</span>
                <span className="text-slate-700">·</span>
                <span>by Ihea leads imóveis</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[10px] uppercase tracking-widest text-slate-600">v2.0</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ===== LADO DIREITO — FORMULÁRIO ===== */}
      <div className={`
        flex-1 bg-white flex flex-col
        ${isMobile ? 'min-h-screen' : 'h-screen overflow-y-auto scrollbar-thin'}
      `}>
        {/* Mobile header */}
        <div className={`lg:hidden sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-slate-100 px-5 py-4`}>
          <PropZynLogo size="sm" dark />
        </div>

        {/* Form area */}
        <div className={`
          flex-1 flex items-start justify-center
          ${isMobile ? 'px-5 pt-8 pb-10' : 'px-8 py-12 lg:px-16'}
        `}>
          <div className={`w-full ${isMobile ? 'max-w-full' : 'max-w-[420px]'}`}>

            {/* Desktop: mini logo mark */}
            <div className="hidden lg:flex items-center gap-2 mb-10">
              <div className="w-6 h-px bg-gradient-to-r from-blue-600 to-violet-600" />
              <span className="text-[11px] uppercase tracking-[0.25em] text-slate-400 font-medium">
                Acesso seguro
              </span>
            </div>

            {/* Headings */}
            <motion.div
              key={mode + '-heading'}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-7"
            >
              <h3 className="font-syne text-2xl font-bold text-slate-900 leading-tight">
                {mode === MODES.LOGIN && 'Bem-vindo de volta'}
                {mode === MODES.SIGNUP && 'Criar sua conta'}
                {mode === MODES.FORGOT_PASSWORD && 'Recuperar acesso'}
                {mode === MODES.RESET_PASSWORD && 'Nova senha'}
                {mode === MODES.VERIFY_EMAIL && 'Verificar email'}
                {mode === MODES.ONBOARDING && 'Personalizar conta'}
              </h3>
              <p className="text-sm text-slate-500 mt-1.5 font-light">
                {mode === MODES.LOGIN && 'Acesse sua conta para continuar gerenciando seus imóveis'}
                {mode === MODES.SIGNUP && 'Comece sua jornada imobiliária gratuitamente'}
                {mode === MODES.FORGOT_PASSWORD && 'Enviaremos as instruções para o seu email'}
                {mode === MODES.RESET_PASSWORD && 'Escolha uma senha forte e segura'}
                {mode === MODES.VERIFY_EMAIL && 'Confirme seu email para ativar a conta'}
                {mode === MODES.ONBOARDING && 'Configure a plataforma para o seu perfil'}
              </p>
            </motion.div>

            {/* Alerts */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                  className="mb-5 overflow-hidden"
                >
                  <div className="p-3.5 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </motion.div>
              )}
              {successMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                  className="mb-5 overflow-hidden"
                >
                  <div className="p-3.5 bg-emerald-50 border border-emerald-100 rounded-xl flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-emerald-700">{successMessage}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Modes */}
            <AnimatePresence mode="wait">
              {mode === MODES.LOGIN && renderLogin()}
              {mode === MODES.SIGNUP && renderSignUp()}
              {mode === MODES.FORGOT_PASSWORD && renderForgotPassword()}
              {mode === MODES.RESET_PASSWORD && renderResetPassword()}
              {mode === MODES.VERIFY_EMAIL && renderVerifyEmail()}
              {mode === MODES.ONBOARDING && renderOnboarding()}
            </AnimatePresence>

            {/* Mobile footer */}
            <div className={`lg:hidden mt-10 pt-6 border-t border-slate-100 text-center`}>
              <p className="text-[11px] text-slate-400">
                © {new Date().getFullYear()} PropZyn · by Ihea leads imóveis
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(LoginPremium);